import type { Fill, TemplateDoc } from '../template';

/**
 * Colours for generated motion graphics, taken from the template itself so panels, rings and
 * title cards look like part of the design (and follow its brand kit).
 */
export interface MotionPalette {
  /** The most vivid colour of the design. */
  accent: string;
  /** A second vivid colour, different enough from the accent. */
  accent2: string;
  dark: string;
  light: string;
  /** Readable on the template's background (backdrops, marquees). */
  ink: string;
}

export const MOTION_PALETTE_KEYS = ['accent', 'accent2', 'dark', 'light', 'ink'] as const;
export type PaletteKey = (typeof MOTION_PALETTE_KEYS)[number];

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Plain colour from a colour or a brand token ("brand:slot|fallback"). */
function plain(c: string): string {
  return c.startsWith('brand:') ? c.slice(c.indexOf('|') + 1) : c;
}

export function parseColor(input: string): (Rgb & { a: number }) | null {
  const c = plain(input).trim().toLowerCase();
  let m = /^#([0-9a-f]{3,8})$/.exec(c);
  if (m) {
    let h = m[1];
    if (h.length === 3 || h.length === 4) h = [...h].map((x) => x + x).join('');
    if (h.length !== 6 && h.length !== 8) return null;
    const n = (i: number) => parseInt(h.slice(i, i + 2), 16);
    return { r: n(0), g: n(2), b: n(4), a: h.length === 8 ? n(6) / 255 : 1 };
  }
  m = /^rgba?\(([^)]+)\)$/.exec(c);
  if (m) {
    const [r, g, b, a] = m[1].split(/[\s,/]+/).map(Number);
    if ([r, g, b].some((v) => Number.isNaN(v))) return null;
    return { r, g, b, a: a === undefined || Number.isNaN(a) ? 1 : a };
  }
  return null;
}

const hex = ({ r, g, b }: Rgb) =>
  '#' +
  [r, g, b]
    .map((v) =>
      Math.round(Math.max(0, Math.min(255, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('');

function luminance({ r, g, b }: Rgb): number {
  const f = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

function hsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const R = r / 255;
  const G = g / 255;
  const B = b / 255;
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === R ? (G - B) / d + (G < B ? 6 : 0) : max === G ? (B - R) / d + 2 : (R - G) / d + 4;
  h *= 60;
  return { h, s, l };
}

function hueDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/** Lighter (k > 0) or darker (k < 0) version of a colour. */
export function shade(color: string, k: number): string {
  const c = parseColor(color);
  if (!c) return color;
  const t = k > 0 ? 255 : 0;
  const a = Math.abs(k);
  return hex({ r: c.r + (t - c.r) * a, g: c.g + (t - c.g) * a, b: c.b + (t - c.b) * a });
}

/** Colour with alpha, as rgba(). */
export function withAlpha(color: string, alpha: number): string {
  const c = parseColor(color);
  if (!c) return color;
  return `rgba(${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)}, ${Math.round(alpha * 1000) / 1000})`;
}

function fillColors(fill: Fill | null | undefined): string[] {
  if (!fill) return [];
  if (fill.type === 'solid') return [fill.color];
  return fill.stops.map((s) => s.color);
}

export function docPalette(doc: TemplateDoc): MotionPalette {
  const canvas = Math.max(1, doc.width * doc.height);
  const weights = new Map<string, { rgb: Rgb; w: number }>();
  const add = (color: string | null | undefined, w: number) => {
    if (!color) return;
    const c = parseColor(color);
    if (!c || c.a < 0.35) return;
    const key = hex(c);
    const prev = weights.get(key);
    weights.set(key, { rgb: c, w: (prev?.w ?? 0) + w });
  };
  for (const c of fillColors(doc.background)) add(c, 1);
  for (const l of doc.layers) {
    if (!l.visible) continue;
    const area = Math.min(1, (l.width * l.height) / canvas);
    if (l.type === 'shape' || l.type === 'group')
      for (const c of fillColors(l.fill)) add(c, area + 0.05);
    if (l.type === 'text') {
      // Text colours count a lot: a small yellow price is often the accent of the design
      add(l.color, 0.08 + Math.min(0.4, l.fontSize / 400));
      for (const c of fillColors(l.background)) add(c, area + 0.1);
      if (l.highlight) add(l.highlight.color, 0.2);
    }
    if (l.type === 'qr') add(l.foreground, 0.02);
  }
  const all = [...weights.values()];
  const vivid = all
    .map((x) => ({ ...x, hsl: hsl(x.rgb) }))
    .filter((x) => x.hsl.s > 0.28 && x.hsl.l > 0.18 && x.hsl.l < 0.86)
    .sort((a, b) => b.w * (0.4 + b.hsl.s) - a.w * (0.4 + a.hsl.s));
  const byLum = [...all].sort((a, b) => luminance(a.rgb) - luminance(b.rgb));
  const darkest = byLum[0];
  const lightest = byLum[byLum.length - 1];
  const dark = darkest && luminance(darkest.rgb) < 0.08 ? hex(darkest.rgb) : '#0f0f14';
  const light = lightest && luminance(lightest.rgb) > 0.75 ? hex(lightest.rgb) : '#ffffff';
  const accent = vivid[0] ? hex(vivid[0].rgb) : '#6d5dfc';
  const second = vivid.find((x) => vivid[0] && hueDistance(x.hsl.h, vivid[0].hsl.h) > 35);
  const accent2 = second
    ? hex(second.rgb)
    : vivid[0]
      ? hex(rotateHue(vivid[0].rgb, 40))
      : '#ff5c8a';
  // Background colour: the doc background, or a full-bleed shape over it
  let bg = fillColors(doc.background)[0];
  for (const l of doc.layers)
    if (
      !l.parentId &&
      l.visible &&
      (l.type === 'shape' || l.type === 'group') &&
      l.width * l.height >= canvas * 0.85
    )
      bg = fillColors(l.fill)[0] ?? bg;
  const bgRgb = bg ? parseColor(bg) : null;
  const ink = bgRgb && luminance(bgRgb) > 0.4 ? dark : light;
  return { accent, accent2, dark, light, ink };
}

/** WCAG contrast ratio of two colours (1..21). */
export function contrast(a: string, b: string): number {
  const x = parseColor(a);
  const y = parseColor(b);
  if (!x || !y) return 21;
  const l1 = luminance(x);
  const l2 = luminance(y);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/** The first colour readable on `bg`, or the last one. */
export function readable(bg: string, ...colors: string[]): string {
  return colors.find((c) => contrast(c, bg) >= 2.6) ?? colors[colors.length - 1];
}

function rotateHue(c: Rgb, deg: number): Rgb {
  const { h, s, l } = hsl(c);
  const H = (((h + deg) % 360) + 360) % 360;
  const C = (1 - Math.abs(2 * l - 1)) * s;
  const X = C * (1 - Math.abs(((H / 60) % 2) - 1));
  const m = l - C / 2;
  const [r, g, b] =
    H < 60
      ? [C, X, 0]
      : H < 120
        ? [X, C, 0]
        : H < 180
          ? [0, C, X]
          : H < 240
            ? [0, X, C]
            : H < 300
              ? [X, 0, C]
              : [C, 0, X];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}

/** Resolve a colour parameter: a palette key ("accent") or a literal colour. */
export function paletteColor(value: unknown, palette: MotionPalette, fallback: PaletteKey): string {
  if (typeof value === 'string') {
    if ((MOTION_PALETTE_KEYS as readonly string[]).includes(value))
      return palette[value as PaletteKey];
    if (parseColor(value)) return plain(value);
  }
  return palette[fallback];
}
